import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import { Color } from '../../../../core/enums/Color';
import { isMobileScreenSize } from '../../../../core/utils/isMobileScreenSize';
import Select from '../../../../components/form/select/Select';
import { generateTimeOptions } from '../../../../components/form/range-time-picker/time-picker/TimePicker';
import { DATE_FORMAT_HOURS_MINUTES, DATE_FORMAT_ISO } from '../../../../core/constants/date';
import Input from '../../../../components/form/input/Input';

import './BookingChangeStatusModal.scss';

interface Props {
  message: string;
  isOpen: boolean;
  closeModal: () => void;
  onConfirm: (bookingDoneAt?: string) => void;
  showDateTimeFields?: boolean;
}

const BookingChangeStatusModal: React.FC<Props> = ({ message, isOpen, closeModal, onConfirm, showDateTimeFields = false }) => {
  const { t } = useTranslation();
  const isMobileScreen = isMobileScreenSize();

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | undefined>();
  const [dateError, setDateError] = useState<string | undefined>();
  const [timeError, setTimeError] = useState<string | undefined>();

  const availableTimeOptions = useMemo(() => {
    return generateTimeOptions(undefined, undefined, true, 15).map((item) => ({
      value: format(item.date, DATE_FORMAT_HOURS_MINUTES),
      text: format(item.date, DATE_FORMAT_HOURS_MINUTES)
    }));
  }, []);

  const handleConfirm = () => {
    if (showDateTimeFields) {
      if (!selectedDate || !selectedTime) {
        setDateError(!selectedDate ? t('booking.status.changeStatus.date.error') : undefined);
        setTimeError(!selectedTime ? t('booking.status.changeStatus.time.error') : undefined);
        return;
      }

      const [hours, minutes] = selectedTime.split(':').map(Number);
      const bookingDateTime = new Date(selectedDate);
      bookingDateTime.setHours(hours, minutes, 0, 0);

      onConfirm(bookingDateTime.toISOString());
      closeModal();
      return;
    }

    onConfirm();
    closeModal();
  };

  const title = <h3 className="modal-title">{t('booking.modal.changeStatus.title')}</h3>;
  const buttons = (
    <div className="modal-buttons">
      <Button onClick={closeModal} buttonTypes={!isMobileScreen ? ['transparent'] : ['outline', 'block']} color={Color.Blue}>
        {t('common.cancel')}
      </Button>
      <Button onClick={handleConfirm} buttonTypes={!isMobileScreen ? ['primary'] : ['primary', 'block']} color={Color.Blue}>
        {t('common.confirm')}
      </Button>
    </div>
  );

  return (
    <Modal
      className={`booking-change-status-modal ${isMobileScreen ? 'modal--small-mobile booking-change-status-modal--mobile' : ''}`}
      isOpen={isOpen}
      closeModal={closeModal}
      title={title}
      toolbar={buttons}
      size="small"
    >
      <span>{t(message)}</span>
      {showDateTimeFields && (
        <div className="booking-change-status-modal__datetime-fields">
          <div className="booking-change-status-modal__field-wrapper">
            <Input
              label={t('booking.deliveryDate')}
              id="delivery-date"
              name="deliveryDate"
              type="date"
              value={selectedDate ? format(selectedDate, DATE_FORMAT_ISO) : ''}
              onChange={(e) => {
                setSelectedDate(new Date(e.target.value));
                setDateError(undefined);
              }}
              required
            />
            {dateError && <span className="booking-change-status-modal__error-message">{dateError}</span>}
          </div>
          <div className="booking-change-status-modal__field-wrapper">
            <Select
              id="start-time"
              name="start-time"
              label={t('booking.deliveryTime')}
              options={availableTimeOptions}
              value={selectedTime}
              onChange={(option) => {
                if (Array.isArray(option)) {
                  [option] = option; // eslint-disable-line no-param-reassign
                }
                setSelectedTime(option.value);
              }}
              placeholder={t('common.selectTimePlaceholder')}
              required
              showSearch={false}
            />
            {timeError && <span className="booking-change-status-modal__error-message">{timeError}</span>}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BookingChangeStatusModal;
